/**
 * Système de badges de rentabilité basé sur la marge brute
 * Utilise les seuils harmonisés BTP France (FFB, Bpifrance 2024)
 * Source unique de vérité : calculs-rentabilite.ts
 *
 * NOUVEAUTÉ : Supporte les seuils dynamiques par métier
 * Les métiers spécialisés (plombier, électricien, etc.) ont des seuils Cerfrance
 * Les métiers généralistes (maçon, TCE, etc.) utilisent les seuils BTP génériques
 */

import { SEUILS_MARGE, getSeuilsParMetier } from '@/lib/calculs-rentabilite';
import { getBenchmarkMetier } from '@/data/benchmarkMetiers';

export interface RentabilityBadge {
  label: string;
  emoji: string;
  color: string;
  bgColor: string;
  urgency: 'none' | 'low' | 'medium' | 'high' | 'critical';
  message: string;
}

/**
 * Retourne le badge de rentabilité selon le pourcentage de marge brute
 * 7 niveaux basés sur les benchmarks BTP France ou du métier spécifique
 *
 * @param profitabilityPct - Pourcentage de marge
 * @param metierKey - Clé du métier (optionnel, utilise seuils génériques si non fourni)
 */
export const getRentabilityBadge = (
  profitabilityPct: number,
  metierKey?: string
): RentabilityBadge => {
  // Récupérer les seuils adaptés au métier
  const seuils = getSeuilsParMetier(metierKey);
  const metier = metierKey ? getBenchmarkMetier(metierKey) : null;
  const metierName = metier?.metier_name || 'BTP';

  // 🏆 Rentabilité Exceptionnelle - Top 10% du secteur
  if (profitabilityPct >= seuils.exceptionnel) {
    return {
      label: 'Rentabilité Exceptionnelle',
      emoji: '🏆',
      color: 'text-emerald-700 dark:text-emerald-300',
      bgColor: 'bg-emerald-100 dark:bg-emerald-900/30 border-emerald-600',
      urgency: 'none',
      message: `Top 10% ${metierName} ! Marge ≥ ${seuils.exceptionnel}%.`
    };
  }

  // ✅ Excellente Rentabilité - Au-dessus moyenne
  if (profitabilityPct >= seuils.excellent) {
    return {
      label: 'Excellente Rentabilité',
      emoji: '✅',
      color: 'text-green-700 dark:text-green-300',
      bgColor: 'bg-green-100 dark:bg-green-900/30 border-green-500',
      urgency: 'none',
      message: `Au-dessus de la moyenne ${metierName} (≥ ${seuils.excellent}%).`
    };
  }

  // 👍 Bonne Rentabilité - Objectif atteint
  if (profitabilityPct >= seuils.bon) {
    return {
      label: 'Bonne Rentabilité',
      emoji: '👍',
      color: 'text-green-600 dark:text-green-400',
      bgColor: 'bg-green-50 dark:bg-green-900/20 border-green-400',
      urgency: 'none',
      message: `Objectif atteint pour ${metierName} (≥ ${seuils.bon}%).`
    };
  }

  // ⚠️ Rentabilité Correcte - Dans la moyenne
  if (profitabilityPct >= seuils.correct) {
    return {
      label: 'Rentabilité Correcte',
      emoji: '⚠️',
      color: 'text-yellow-700 dark:text-yellow-300',
      bgColor: 'bg-yellow-100 dark:bg-yellow-900/30 border-yellow-500',
      urgency: 'low',
      message: `Dans la moyenne ${metierName}. À surveiller.`
    };
  }

  // 🟠 Rentabilité Fragile - En dessous moyenne
  if (profitabilityPct >= seuils.fragile) {
    return {
      label: 'Rentabilité Fragile',
      emoji: '🟠',
      color: 'text-orange-700 dark:text-orange-300',
      bgColor: 'bg-orange-100 dark:bg-orange-900/30 border-orange-500',
      urgency: 'medium',
      message: `Marge fragile pour ${metierName}. Optimisation nécessaire.`
    };
  }

  // 🚨 Rentabilité Critique - Marge quasi nulle
  if (profitabilityPct >= seuils.critique) {
    return {
      label: 'Rentabilité Critique',
      emoji: '🚨',
      color: 'text-red-700 dark:text-red-300',
      bgColor: 'bg-red-100 dark:bg-red-900/30 border-red-500',
      urgency: 'high',
      message: `Marge critique (≥ ${seuils.critique}%). Action urgente !`
    };
  }

  // ❌ En Déficit (< 0%)
  return {
    label: 'Déficit',
    emoji: '❌',
    color: 'text-red-800 dark:text-red-200',
    bgColor: 'bg-red-200 dark:bg-red-900/50 border-red-700',
    urgency: 'critical',
    message: '🚨 DÉFICIT : Le chantier perd de l\'argent !'
  };
};
